/**
 * Date comparison utilities.
 *
 * --------------------------------------------------------------------------
 * WHY THIS FILE EXISTS (for code reviewers asking "is this overkill?"):
 * --------------------------------------------------------------------------
 *
 * This file exports a single ~10-line function. Some reviewers may flag it as
 * over-engineering or "premature abstraction." It is intentional and was
 * extracted to address a specific class of bug, not for stylistic reasons.
 *
 * The codebase has a documented history of bugs (#73, #80, #83, #84) caused
 * by change-detection logic drifting between production code and tests.
 * Tests had local "approximations" of the real logic; the real logic
 * eventually diverged from the test helper, and the tests kept passing while
 * production silently broke. The fix pattern across all of them: extract the
 * critical logic into a pure function, import it in BOTH production and
 * tests, so they cannot drift.
 *
 * Project priorities (from CLAUDE.md):
 *   - Software working end-to-end without silent failures or phantom updates
 *     to FileMaker is the top priority.
 *   - Stylistic concerns (file count, indirection, "this is too small to be
 *     its own file") are explicitly secondary to that priority.
 *
 * --------------------------------------------------------------------------
 * THE BUG THIS HELPS PREVENT (#84):
 * --------------------------------------------------------------------------
 *
 * FileMaker stores dates zero-padded ("04/26/2026"). The Scheduler formats
 * them via toLocaleDateString("en-us") which strips leading zeros
 * ("4/26/2026"). When the Scheduler sent dates back to MongoDB, FileMaker
 * saw "04/26/2026" → "4/26/2026" as a value change and triggered phantom
 * scheduling updates downstream (TurboQ, etc.).
 *
 * This function does a SEMANTIC comparison via Date.getTime() so format
 * differences don't produce false positives. Use it anywhere you need to
 * compare a date-like value coming from MongoDB/FileMaker against a date-like
 * value produced inside the Scheduler.
 */

/**
 * Returns true if two date-like values represent the same calendar date,
 * regardless of string formatting differences (e.g., "04/26/2026" vs "4/26/2026").
 *
 * - Empty values (null, undefined, "") are treated as equal to each other,
 *   and not equal to any non-empty value. This correctly distinguishes
 *   "scheduled" from "unscheduled."
 * - Falls back to strict string equality if either value cannot be parsed
 *   as a Date. This is defensive — should not happen in practice but
 *   prevents NaN === NaN (false) from producing a confusing result.
 *
 * @param {string|null|undefined} a - First date value (e.g., from MongoDB)
 * @param {string|null|undefined} b - Second date value (e.g., from Scheduler state)
 * @returns {boolean} true if the dates are semantically equal
 */
export function datesEqual(a, b) {
  const aEmpty = !a || a === "";
  const bEmpty = !b || b === "";
  if (aEmpty && bEmpty) return true;
  if (aEmpty || bEmpty) return false;
  const aMs = new Date(a).getTime();
  const bMs = new Date(b).getTime();
  if (isNaN(aMs) || isNaN(bMs)) return a === b;
  return aMs === bMs;
}
